import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

function readCachedUser() {
  try {
    const raw = localStorage.getItem("afr_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSession({ token, user }) {
  if (token) localStorage.setItem("afr_token", token);
  if (user) {
    localStorage.setItem("afr_user", JSON.stringify(user));
    if (user.role) localStorage.setItem("afr_role", user.role);
  }
}

export function AuthProvider({ children }) {
  // Hydrate immediately from localStorage if we have it
  const cached = readCachedUser();
  const [user, setUser] = useState(cached || null);
  const [loading, setLoading] = useState(true);

  const loadMe = async () => {
    const token = localStorage.getItem("afr_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      writeSession({ user: data });
    } catch (e) {
      // Only nuke session on genuine auth failures (401/403). For network
      // hiccups / 5xx we trust the localStorage hydration and silently retry
      // on the next mount — critical for PWA offline-to-online transitions.
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        localStorage.clear();
        setUser(false);
      } else if (!cached) {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMe();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    writeSession({ token: data.token, user: data.user });
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    writeSession({ token: data.token, user: data.user });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("afr_fcm_token");
      if (token) {
        try {
          await api.post("/notifications/unregister-token", { token });
        } catch (e) {
          console.warn("[Auth] unregister-token failed (non-fatal):", e?.message);
        }
      }
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("[Auth] logout endpoint failed (non-fatal):", e?.message);
    }
    localStorage.clear();
    setUser(false);
  };

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    writeSession({ user: data });
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
